import { Activity, Pause, Play, RefreshCw } from "lucide-react";
import { useIsFetching } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { useMonitoringInstances, useMonitoringSummary } from "./api";
import { Ago, StatePill } from "./components/primitives";
import { AREA_TAB } from "./findings";
import { MONITORING_TABS, useMonitoring, type MonitoringTab } from "./monitoring-context";
import { MONITORING_WINDOWS, type MonitoringWindow, type Severity } from "./types";
import { OverviewTab } from "./tabs/overview-tab";
import { PodsTab } from "./tabs/pods-tab";
import { CamerasTab } from "./tabs/cameras-tab";
import { PipelineTab } from "./tabs/pipeline-tab";
import { UnevaluatedTab } from "./tabs/unevaluated-tab";
import { BbTab } from "./tabs/bb-tab";
import { RequestsTab } from "./tabs/requests-tab";
import { EventsTab } from "./tabs/events-tab";
import { BackendTab } from "./tabs/backend-tab";

const TAB_CONTENT: Record<MonitoringTab, () => JSX.Element | null> = {
  overview: OverviewTab,
  pods: PodsTab,
  cameras: CamerasTab,
  pipeline: PipelineTab,
  unevaluated: UnevaluatedTab,
  bb: BbTab,
  requests: RequestsTab,
  events: EventsTab,
  backend: BackendTab,
};

/** The board: header with the overall state and controls, a sticky tab bar, one tab at a time. */
export function MonitoringBoard() {
  const { t } = useLocalization();
  const { tab, setTab } = useMonitoring();
  const summary = useMonitoringSummary();
  const status = summary.data?.status;

  // the worst open finding per tab, shown as a dot on the tab
  const tabSeverity = new Map<MonitoringTab, Severity>();
  for (const finding of summary.data?.findings ?? []) {
    const target = AREA_TAB[finding.area];
    if (!target || finding.severity === "info") continue;
    if (tabSeverity.get(target) !== "critical") tabSeverity.set(target, finding.severity);
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <div className="flex flex-col gap-3 px-4 pt-4 lg:px-6 lg:pt-6">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <div className="flex min-w-0 items-center gap-2">
            <Activity className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
            <h1 className="text-2xl font-semibold tracking-tight">{t("monitoring.title")}</h1>
          </div>
          {status && (
            <StatePill tone={status === "ok" ? "ok" : status === "warning" ? "warn" : "nok"} pulse={status === "critical"}>
              {t(`monitoring.status.${status}`)}
            </StatePill>
          )}
          <div className="ml-auto">
            <RefreshControls />
          </div>
        </div>
        <p className="text-sm text-muted-foreground">{t("monitoring.description")}</p>
      </div>

      <Tabs value={tab} onValueChange={(value) => setTab(value as MonitoringTab)} className="flex min-w-0 flex-col">
        <div className="sticky top-12 z-20 mt-3 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2 lg:px-6">
            <div className="-mx-1 min-w-0 max-w-full flex-1 overflow-x-auto px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <TabsList className="h-9 w-max">
                {MONITORING_TABS.map((key) => {
                  const severity = tabSeverity.get(key);
                  return (
                    <TabsTrigger key={key} value={key} className="gap-1.5 px-2.5 text-xs sm:text-sm">
                      {t(`monitoring.tab.${key}`)}
                      {severity && (
                        <span
                          className={cn(
                            "inline-block h-1.5 w-1.5 rounded-full",
                            severity === "critical" ? "bg-red-500" : "bg-amber-400",
                          )}
                          aria-label={t(`monitoring.severity.${severity}`)}
                        />
                      )}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </div>
            {/* the unevaluated tab has its own period in days and reads the business data, not the metrics */}
            {tab !== "unevaluated" && (
              <div className="flex items-center gap-2">
                <WindowSelect />
                <ScopeSelect />
              </div>
            )}
          </div>
        </div>
        {MONITORING_TABS.map((key) => {
          const Content = TAB_CONTENT[key];
          return (
            <TabsContent key={key} value={key} className="mt-0 min-w-0 px-4 py-4 focus-visible:ring-0 lg:px-6">
              {tab === key && <Content />}
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}

function WindowSelect() {
  const { t } = useLocalization();
  const { window, setWindow } = useMonitoring();
  return (
    <ToggleGroup
      type="single"
      size="sm"
      variant="outline"
      value={window}
      onValueChange={(value) => value && setWindow(value as MonitoringWindow)}
      aria-label={t("monitoring.window")}
      className="gap-0.5"
    >
      {MONITORING_WINDOWS.map((w) => (
        <ToggleGroupItem key={w} value={w} className="h-8 whitespace-nowrap px-2 text-xs sm:px-2.5" aria-label={t(`monitoring.window.long.${w}`)}>
          {t(`monitoring.window.${w}`)}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

function ScopeSelect() {
  const { t } = useLocalization();
  const { scope, setScope } = useMonitoring();
  const instances = useMonitoringInstances();
  const role = instances.data?.role ?? "prod";
  const value = scope.instance ? `instance:${scope.instance}` : scope.all ? "all" : "role";
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <Select
              value={value}
              onValueChange={(next) => {
                if (next === "all") setScope({ instance: null, all: true });
                else if (next === "role") setScope({ instance: null, all: false });
                else setScope({ instance: next.slice("instance:".length), all: false });
              }}
            >
              <SelectTrigger className="h-8 w-[9.5rem] text-xs sm:w-[16rem]" aria-label={t("monitoring.scope")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="role" className="text-xs">{t("monitoring.scope.role", { role })}</SelectItem>
                <SelectItem value="all" className="text-xs">{t("monitoring.scope.all")}</SelectItem>
                {(instances.data?.instances.length ?? 0) > 0 && <SelectSeparator />}
                {instances.data?.instances.map((instance) => (
                  <SelectItem key={instance.instance} value={`instance:${instance.instance}`} className="text-xs">
                    <span className="font-mono">{instance.instance}</span>
                    <span className="ml-1.5 text-muted-foreground">
                      {instance.role}
                      {instance.current ? ` · ${t("monitoring.scope.current")}` : ""}
                    </span>
                  </SelectItem>
                ))}
                {scope.instance && !instances.data?.instances.some((i) => i.instance === scope.instance) && (
                  <SelectItem value={`instance:${scope.instance}`} className="text-xs">
                    {t("monitoring.scope.instance", { instance: scope.instance })}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{t("monitoring.scope.hint")}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function RefreshControls() {
  const { t } = useLocalization();
  const { paused, setPaused, refreshAll } = useMonitoring();
  const fetching = useIsFetching({ queryKey: ["monitoring"] }) > 0;
  const summary = useMonitoringSummary();
  const updatedAt = summary.dataUpdatedAt || null;
  return (
    <div className="flex items-center gap-1.5">
      <span className="hidden text-xs text-muted-foreground sm:inline">
        {paused ? (
          t("monitoring.paused")
        ) : fetching ? (
          t("monitoring.updating")
        ) : updatedAt ? (
          <>
            {t("monitoring.updated", { ago: "" }).trim()} <Ago ts={updatedAt} />
          </>
        ) : (
          t("monitoring.notUpdated")
        )}
      </span>
      <Button
        variant="outline"
        size="sm"
        className="h-8 w-8 p-0"
        onClick={refreshAll}
        title={t("monitoring.refreshNow")}
        aria-label={t("monitoring.refreshNow")}
      >
        <RefreshCw className={cn("h-4 w-4", fetching && "animate-spin")} />
      </Button>
      <Button
        variant={paused ? "secondary" : "outline"}
        size="sm"
        className="h-8 gap-1.5 px-2.5 text-xs"
        onClick={() => setPaused(!paused)}
        aria-pressed={paused}
        title={paused ? t("monitoring.resume") : t("monitoring.pause")}
      >
        {paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
        <span className="hidden sm:inline">{paused ? t("monitoring.resume") : t("monitoring.pause")}</span>
      </Button>
    </div>
  );
}
